import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, lotteryService, type BetSlip } from '../../services/lotteryService'
import * as walletService from '../../services/walletService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'

const PAGE_SIZE = 20

const SLIP_TABS = [
  { id: 'wait', label: 'รอผล', icon: 'far fa-clock', filter: 'waiting' as const },
  { id: 'done', label: 'ผลออกแล้ว', icon: 'far fa-check-circle', filter: 'checked' as const },
  { id: 'today', label: 'โพยวันนี้', icon: 'far fa-sun', filter: 'today' as const },
  { id: 'history', label: 'ดูย้อนหลัง', icon: 'fas fa-calendar-alt', filter: 'all' as const },
]

const statusLabel: Record<string, string> = {
  pending: 'รอผล',
  confirmed: 'รอผล',
  won: 'ถูกรางวัล',
  lost: 'ไม่ถูกรางวัล',
  partial_win: 'ถูกรางวัลบางส่วน',
  cancelled: 'ยกเลิก',
  refunded: 'คืนเงิน',
}

const statusClass: Record<string, string> = {
  pending: 'bg_primary',
  confirmed: 'bg_primary',
  won: 'bg_success',
  lost: 'bg_primary',
  partial_win: 'bg_success',
  cancelled: 'bg_primary',
  refunded: 'bg_primary',
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function isToday(dateStr: string): boolean {
  try {
    const d = new Date(dateStr)
    const now = new Date()
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    )
  } catch {
    return false
  }
}

function getUserId(): string | null {
  const token = localStorage.getItem('accessToken')
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.sub ?? payload.id ?? payload.userId ?? null
    } catch {
      // ignore
    }
  }
  try {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const u = JSON.parse(userStr) as { id?: string; _id?: string }
      return u?.id ?? u?._id ?? null
    }
  } catch {
    // ignore
  }
  return null
}

function getCustomerCancelWindowMinutes(slip: BetSlip): number {
  const configured = Number(slip.customerCancelWindowMinutes)
  return Number.isFinite(configured) && configured > 0
    ? configured
    : CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
}

function canCustomerCancelSlip(slip: BetSlip): boolean {
  const status = String(slip.status || '').toLowerCase()
  if (slip.isSettled || ['cancelled', 'refunded'].includes(status)) return false

  const createdAt = slip.createdAt ? new Date(slip.createdAt).getTime() : NaN
  if (!Number.isFinite(createdAt)) return false

  return Date.now() - createdAt <= getCustomerCancelWindowMinutes(slip) * 60 * 1000
}

/** หน้า โพยหวย — ดึง API แบบ user-classic TicketAllPage, UI แบบ modern */
const ModernLobbyHuaySlipPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const [activeTabId, setActiveTabId] = useState('wait')
  const [slips, setSlips] = useState<BetSlip[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** ค้นหาโพย — กรองตามรหัสโพย, ชื่อหวย, รอบ (ใช้ในทุกแท็บ) */
  const [searchQuery, setSearchQuery] = useState('')

  const currentTab = SLIP_TABS.find((t) => t.id === activeTabId) ?? SLIP_TABS[0]

  const loadSlips = useCallback(
    async (pageNum: number = 1) => {
      const userId = getUserId()
      if (!userId) {
        setSlips([])
        setTotal(0)
        setLoading(false)
        setError('กรุณาเข้าสู่ระบบ')
        return
      }
      try {
        setLoading(true)
        setError(null)
        const skip = (pageNum - 1) * PAGE_SIZE
        const isSettled =
          currentTab.filter === 'waiting'
            ? false
            : currentTab.filter === 'checked'
              ? true
              : undefined

        const { slips: data, total: totalCount } = await lotteryService.getUserSlips(userId, {
          limit: currentTab.filter === 'today' ? 200 : PAGE_SIZE,
          skip: currentTab.filter === 'today' ? 0 : skip,
          ...(isSettled !== undefined && { isSettled }),
        })

        if (currentTab.filter === 'today') {
          const todaySlips = data.filter((s) => s.createdAt && isToday(s.createdAt))
          setSlips(todaySlips)
          setTotal(todaySlips.length)
          setPage(1)
        } else {
          setSlips(data)
          setTotal(totalCount)
          setPage(pageNum)
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'โหลดโพยไม่สำเร็จ'
        setError(msg)
        setSlips([])
        setTotal(0)
      } finally {
        setLoading(false)
      }
    },
    [currentTab.filter]
  )

  useEffect(() => {
    loadSlips(1)
  }, [loadSlips])

  const filtered = useMemo(() => {
    if (currentTab.filter === 'all') return slips
    if (currentTab.filter === 'waiting') return slips.filter((s) => !s.isSettled)
    if (currentTab.filter === 'checked') return slips.filter((s) => s.isSettled === true)
    return slips
  }, [slips, currentTab.filter])

  /** กรองด้วยคำค้น — รหัสโพย, ชื่อหวย, รอบ, วันที่ (ใช้ได้ทุกแท็บ) */
  const searchFiltered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return filtered
    return filtered.filter((s) => {
      const slipId = String(s.slipId ?? s._id ?? '').toLowerCase()
      const huayName = String(s.huayName ?? '').toLowerCase()
      const roundNumber = String(s.roundNumber ?? '').toLowerCase()
      const createdAt = s.createdAt ? formatDate(s.createdAt).toLowerCase() : ''
      return (
        slipId.includes(q) ||
        huayName.includes(q) ||
        roundNumber.includes(q) ||
        createdAt.includes(q)
      )
    })
  }, [filtered, searchQuery])

  const totalPages =
    currentTab.filter === 'today' ? 1 : Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasPrev = page > 1
  const hasNext = page < totalPages

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    loadSlips(nextPage)
  }

  const handleViewDetail = (slipId: string) => {
    navigate(`/lobby-huay/slip/detail/${slipId}`)
  }

  const handleCancelSlip = async (slip: BetSlip) => {
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)
    if (!canCustomerCancelSlip(slip)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนโพยได้',
        text: `คืนโพยได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนโพย',
      html: `คุณต้องการคืนโพย <strong>${slip.slipId ?? slip._id ?? '-'}</strong> ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของโพยกลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนโพย',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6b7280',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelSlip(String(slip.slipId ?? slip._id ?? ''))
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนโพยไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
        confirmButtonColor: '#0d6efd',
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'คืนโพยสำเร็จ',
      text: response.message || 'ระบบคืนโพยเรียบร้อยแล้ว',
      confirmButtonColor: '#0d6efd',
    })

    await loadSlips(currentTab.filter === 'today' ? 1 : page)
  }

  const tabNote =
    currentTab.filter === 'waiting'
      ? 'โพยในแท็บนี้จะย้ายไป "ผลออกแล้ว" เมื่อมีการประกาศผลแพ้/ชนะ'
      : currentTab.filter === 'checked'
        ? 'โพยที่ประกาศผลแพ้/ชนะแล้ว'
        : null

  if (loading && slips.length === 0) {
    return (
      <div className="c_quinary text-center py-5">
        <h3 className="c_tertiary font_md mb-3">โพยหวย</h3>
        <div className="border_5 bg_secondary p-4">
          <i className="fas fa-spinner fa-spin fs-24 c_primary" />
          <p className="c_secondary mt-2">กำลังโหลดโพย...</p>
        </div>
      </div>
    )
  }

  if (error && slips.length === 0) {
    return (
      <div className="c_quinary text-center py-5">
        <h3 className="c_tertiary font_md mb-3">โพยหวย</h3>
        <div className="border_5 bg_secondary p-4">
          <p className="c_primary">{error}</p>
          <button
            type="button"
            className="mt-2 px-3 py-1 bg_gd_quaternary c_tertiary border_5 border-0"
            onClick={() => loadSlips(1)}
          >
            โหลดใหม่
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">โพยหวย</h3>
      </div>

      {tabNote && (
        <p className="small c_tertiary mb-2 px-1 mt-2">
          <i className="fas fa-info-circle me-1" />
          {tabNote}
        </p>
      )}

      <div
        className="nav nav-pills nav-pills-ph mt-3 mb-3"
        id="v-pills-tab"
        role="tablist"
        aria-orientation="vertical"
      >
        {SLIP_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            className={`nav-link${activeTabId === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTabId(tab.id)}
          >
            <div className="text-center">
              <h3 className="">
                <i className={tab.icon} />
              </h3>
              <h6 className="font_md">{tab.label}</h6>
            </div>
          </button>
        ))}
      </div>

      <div className="tab-content" id="v-pills-tabContent">
        {/* ค้นหาโพย — ใช้ได้ทุกแท็บ */}
        <div className="col-12 px-1 mb-2">
          <div className="input-group border_5">
            <span className="input-group-text bg_secondary bdc_primary c_tertiary border_5">
              <i className="fas fa-search" />
            </span>
            <input
              type="text"
              className="form-control bg_secondary c_primary border_5"
              placeholder="ค้นหารหัสโพย, ชื่อหวย, รอบ, วันที่..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="ค้นหาโพย"
            />
            {searchQuery.trim() && (
              <button
                type="button"
                className="btn bg_secondary bdc_primary c_tertiary border_5"
                onClick={() => setSearchQuery('')}
                aria-label="ล้างคำค้น"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>
          <small className="c_tertiary">
            {searchQuery.trim()
              ? `แสดง ${searchFiltered.length} รายการ (จาก ${filtered.length} รายการในแท็บนี้)`
              : totalPages > 1
                ? `โหลด ${filtered.length} รายการ จากทั้งหมด ${total} รายการ (${PAGE_SIZE} รายการต่อหน้า)`
                : `โหลด ${filtered.length} รายการ`}
          </small>
        </div>

        <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
          {searchFiltered.length === 0 ? (
            <div className="col-12 px-1">
              <div className="border_5 bg_secondary py-5 text-center">
                <h5 className="c_primary">
                  {filtered.length === 0
                    ? 'ไม่มีข้อมูลโพย'
                    : 'ไม่พบโพยที่ตรงกับคำค้น ลองเปลี่ยนคำค้นหรือล้างการค้นหา'}
                </h5>
              </div>
            </div>
          ) : (
            searchFiltered.map((slip, index) => {
              const status = (slip.status || 'pending') as string
              const totalWin = Number(slip.totalWinAmount)
              const totalEff = Number(slip.totalEffectiveAmount)
              const winLoss =
                slip.isSettled && !Number.isNaN(totalWin)
                  ? totalWin - (Number.isNaN(totalEff) ? 0 : totalEff)
                  : null
              const displayOrdinal = searchQuery.trim()
                ? index + 1
                : currentTab.filter === 'today'
                  ? index + 1
                  : (page - 1) * PAGE_SIZE + index + 1
              return (
                <div key={slip._id} className="col-12 px-1">
                  <div className="border_5 bdc_secondary bg_secondary p-2">
                    <div className="box_row justify-content-between">
                      <h6 className="c_primary font_md">โพยที่ {displayOrdinal}</h6>
                      <div className="box_row gap-1 align-items-center">
                        {canCustomerCancelSlip(slip) && (
                          <button
                            type="button"
                            className="border-0 bg-warning px-2 py-1 border_5 small"
                            onClick={() => void handleCancelSlip(slip)}
                          >
                            คลิกเพื่อคืนโพย
                          </button>
                        )}
                        <h6 className={`${statusClass[status] || 'bg_primary'} c_tertiary px-1 border_5`}>
                          {statusLabel[status] || status}
                        </h6>
                      </div>
                    </div>
                    <div className="bg_gd_secondary c_tertiary text-center border_5 px-1 bx_sd mt-1 mb-2">
                      <small>
                        <span className="font_md">
                          <i className="fas fa-clipboard-list me-1" /> รหัสโพย
                        </span>{' '}
                        {slip.slipId ?? slip._id ?? '-'}
                      </small>
                    </div>
                    <div
                      className="d-grid gap-2 align-items-center mt-1"
                      style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
                    >
                      <h6 className="c_primary font_md text-center">{slip.huayName ?? '-'}</h6>
                      <small className="c_secondary text-center">รอบที่ {slip.roundNumber ?? '-'}</small>
                    </div>
                    <div
                      className="d-grid gap-2 mt-1"
                      style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
                    >
                      <div className="bg_gd_primary border_5 bdc_secondary p-1 text-center">
                        <div className="bg_op_secondary px-1 py-1 font_rg border_5">
                          <small className="c_tertiary">เงินเดิมพันทั้งหมด</small>
                        </div>
                        <div className="px-1 font_rg c_tertiary">
                          <h4>{walletService.formatCurrency(slip.totalAmount ?? 0)}</h4>
                        </div>
                      </div>
                      <div className="bg_gd_primary border_5 bdc_secondary p-1 text-center">
                        <div className="bg_op_secondary px-1 py-1 font_rg border_5">
                          <small className="c_tertiary">สรุปผลแพ้/ชนะ</small>
                        </div>
                        <div className="px-1 font_rg c_tertiary">
                          <h4>
                            {winLoss != null
                              ? walletService.formatCurrency(winLoss)
                              : slip.isSettled
                                ? walletService.formatCurrency(
                                    (Number(slip.totalWinAmount) || 0) - (Number(slip.totalEffectiveAmount) || 0)
                                  )
                                : '-'}
                          </h4>
                        </div>
                      </div>
                    </div>
                    <div className="box_row justify-content-between mt-1">
                      <small className="c_secondary">
                        เวลาแทง : <i className="fas fa-clock" /> {slip.createdAt ? formatDate(slip.createdAt) : '-'}
                      </small>
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0"
                        onClick={() => handleViewDetail(slip.slipId ?? slip._id)}
                      >
                        <small className="bg_tertiary c_secondary px-1 border_5">
                          <i className="fas fa-search me-1" /> รายละเอียด
                        </small>
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* แสดง pagination เมื่อมีข้อมูลและไม่ได้ค้นหา (รวมกรณี 1 หน้า — จะเห็น "หน้า 1/1" และปุ่ม disabled) */}
        {filtered.length > 0 && !searchQuery.trim() && (
          <div className="box_row justify-content-between align-items-center mt-2 px-1">
            <small className="c_tertiary">
              โพยที่ {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} จาก {total}
            </small>
            <div className="box_row gap-1">
              <button
                type="button"
                className="bg_secondary bdc_primary c_primary px-2 py-1 border_5"
                disabled={!hasPrev}
                onClick={() => goToPage(page - 1)}
              >
                ก่อนหน้า
              </button>
              <span className="c_tertiary px-1">
                หน้า {page} / {totalPages}
              </span>
              <button
                type="button"
                className="bg_secondary bdc_primary c_primary px-2 py-1 border_5"
                disabled={!hasNext}
                onClick={() => goToPage(page + 1)}
              >
                ถัดไป
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ModernLobbyHuaySlipPage
